/* FM Product Compare — bar + comparison table controller.
 * 06-Jun-2026. Paired with sections/fm-compare-bar.liquid and
 * templates/product.compare.liquid.
 *
 * Checkboxes live in the product grid, which the facet flow re-renders
 * wholesale, so we (a) listen for `change` via delegation and (b) re-apply
 * checked state after each grid swap via a MutationObserver.
 *
 * Selection PERSISTS across page loads via sessionStorage (29-Jul-2026).
 * Facets and sort go through AJAX and keep the grid's DOM alive, but
 * pagination does not — the page links are ordinary `<a href="?page=2">`
 * outside the facet form, so page 2 is a full navigation. Selecting two mats
 * on page 1 and paging forward silently emptied the bar. Comparing across
 * pages is the normal case in a 14-product collection, so the selection has
 * to outlive a navigation.
 */
(function () {
  'use strict';

  var root = document.getElementById('fm-compare');
  if (!root) return;

  var MIN = parseInt(root.getAttribute('data-min'), 10) || 2;
  var THEME_MAX = parseInt(root.getAttribute('data-max'), 10) || 4;

  /* Mobile caps the selection lower than desktop (design update, 28-Jul-2026):
   * the compare sheet gives each product a full-width column, and four columns
   * on a 390px screen stops being readable.
   *
   * Two separate settings rather than one, because `compare_max` is shared with
   * desktop and templates/product.compare.liquid — lowering it would silently
   * reduce desktop's limit too. `Math.min` so the mobile cap can never exceed
   * the global one, whichever way an editor sets them. Read once at load; a
   * device does not cross this breakpoint mid-session.
   */
  var MOBILE_MAX = parseInt(root.getAttribute('data-max-mobile'), 10) || 3;
  var isNarrow = window.matchMedia('(max-width: 767px)').matches;
  var MAX = isNarrow ? Math.min(MOBILE_MAX, THEME_MAX) : THEME_MAX;
  var NOUN = root.getAttribute('data-noun') || 'Products';
  var NOUN_LC = NOUN.toLowerCase();

  /* ——— Presentation map — labels + grouping ———
   * Edit freely. `LABELS` overrides the auto-humanised label for a key.
   * `GROUPS` is checked in order (first match wins); any key that matches
   * none falls into the final catch-all. Tests run against the lowercased
   * metafield key. `tagline` is special-cased as the headline "Best for"
   * row and is never grouped. A group header only renders if it has rows. */
  var LABELS = {
    tagline: 'Best for'
  };
  var GROUPS = [
    { title: 'Ratings',     test: function (k) { return /rating/.test(k); } },
    { title: 'Environment', keys: ['suitable_for_wet_areas'], test: function (k) { return /resist/.test(k); } },
    { title: 'Safety',      test: function (k) { return /safety|fire_test|bevel|hazard/.test(k); } },
    { title: 'Dimensions',  keys: ['made_to_measure', 'mat_format'], test: function (k) { return /weight|thick|size|length|width|height|gauge|diameter|dimension/.test(k); } },
    { title: 'Additional features', test: function () { return true; } }
  ];

  /* ——— Mobile compare sheet: the row set ———
   * Brief §6. A FIXED, human-written set of seven rows, deliberately unlike the
   * desktop table below, which enumerates every metafield minus an exclusion
   * list. Enumeration is why raw values leak into the desktop table
   * ("absorption lpm2", the stray apostrophe in "'-20°C to +130°C"); a curated
   * list cannot leak, because anything not named here is simply not shown.
   *
   * Confirmed with Jack 29-Jul-2026:
   *   - `indoor_outdoor` does not exist yet as a metafield. He is populating it.
   *     Until then no product has a value and `hideWhenEmpty` drops the row, so
   *     it appears by itself once the data lands — no code change needed.
   *   - The row set stays in JS rather than moving into product.compare.liquid,
   *     so the compare view keeps serving desktop unchanged.
   *
   * `field` reads an enumerated metafield out of `fields`; `get` reads a
   * top-level scalar that enumeration cannot produce (price, delivery, rating,
   * custom sizing). `diff` supplies the string used to decide whether the
   * products disagree, when that differs from what is displayed.
   */
  var SHEET_ROWS = [
    {
      label: 'Price from',
      priceRow: true,
      get: function (d) { return d.price; },
      /* The figure only. The suffix varies for reasons that are not a real
       * difference — "+ VAT" and "Ex. VAT" both come out of the card's price
       * logic and mean the same thing, so including it would flag two
       * identically-priced mats as differing. */
      diff: function (d) { return d.price; }
    },
    { label: 'Best for', field: 'tagline' },
    { label: 'Delivery', get: function (d) { return d.delivery; } },
    { label: 'Thickness', field: 'thickness_mm', format: millimetres },
    { label: 'Indoor or outdoor', field: 'indoor_outdoor' },
    {
      /* The one row with a specified fallback: absence is itself the answer, so
       * a product with no custom sizing reads "Stock sizes only" rather than a
       * dash. Resolved server-side by snippets/fm-custom-size-label.liquid, the
       * same snippet that labels the card's badge, so the two cannot disagree. */
      label: 'Custom sizing',
      get: function (d) { return d.custom_size; },
      fallback: 'Stock sizes only'
    },
    { label: 'Rating', ratingRow: true, get: function (d) { return d.rating; } }
  ];

  /* "9" → "9mm". Idempotent, and it leaves a value an editor has already
   * written a unit onto alone rather than producing "9mmmm". */
  function millimetres(v) {
    var s = String(v).trim();
    if (!s) return '';
    return /[a-z]/i.test(s) ? s : s + 'mm';
  }

  var introCount = root.querySelector('[data-fm-compare-introcount]');
  var itemsEl = root.querySelector('[data-fm-compare-items]');
  var openBtn = root.querySelector('[data-fm-compare-open]');
  var btnLabel = root.querySelector('[data-fm-compare-btnlabel]');
  var toggle = root.querySelector('[data-fm-compare-toggle]');
  var panel = root.querySelector('.fm-compare__panel');
  var panelTitle = root.querySelector('[data-fm-compare-paneltitle]');
  var closeBtn = root.querySelector('[data-fm-compare-close]');
  var tableEl = root.querySelector('[data-fm-compare-table]');
  var chipTpl = document.getElementById('fm-compare-chip');
  var clearBtn = root.querySelector('[data-fm-compare-clear]');

  /* Compare sheet (mobile). Portalled to <body> below — see openSheet. */
  var sheetRoot = document.getElementById('FmCompareSheet');
  var sheetHead = sheetRoot && sheetRoot.querySelector('[data-fm-compare-sheethead]');
  var sheetRows = sheetRoot && sheetRoot.querySelector('[data-fm-compare-sheetrows]');
  var sheetFooter = sheetRoot && sheetRoot.querySelector('[data-fm-compare-sheetfooter]');
  var sheetPanel = sheetRoot && sheetRoot.querySelector('.fm-comparesheet__sheet');

  var selected = new Map();   // id -> { id, handle, title, image, url }
  var cache = new Map();      // handle -> compare JSON
  var expanded = false;
  var sheetOpen = false;      // `compareView` in the brief's state table

  function track(event, props) {
    try { if (window.posthog && window.posthog.capture) window.posthog.capture(event, props || {}); } catch (e) {}
  }

  /* ——— Persistence across page loads ———
   * sessionStorage, not localStorage: a comparison is a task you finish in one
   * visit, and stale ticks resurfacing days later would be a bug, not a feature.
   * Clearing the tab clears the selection.
   *
   * The KEY carries the collection handle rather than the payload carrying it.
   * A single shared key would mean one selection for the whole site, so simply
   * passing through another compare-enabled collection silently discarded the
   * one you had built up — the write for the empty new collection overwrote it.
   * Keyed per collection, each keeps its own and nothing is destroyed in
   * passing. Entries are per-tab and die with it, so a few are harmless.
   */
  var COLLECTION = root.getAttribute('data-collection') || 'unknown';
  var STORE_KEY = 'fmCompare:' + COLLECTION;

  function persist() {
    try {
      var items = [];
      selected.forEach(function (p) { items.push(p); });
      if (!items.length) {
        sessionStorage.removeItem(STORE_KEY);
        return;
      }
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ items: items }));
    } catch (e) {
      /* Private mode and full quotas both throw. Persistence is an enhancement;
       * losing it must never break selecting. */
    }
  }

  function restore() {
    var raw;
    try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) return;
    var data;
    try { data = JSON.parse(raw); } catch (e) {
      try { sessionStorage.removeItem(STORE_KEY); } catch (e2) {}
      return;
    }
    if (!data || !Array.isArray(data.items)) return;

    data.items.forEach(function (p) {
      // Trim to MAX: the cap can be lowered in theme settings, or the viewport
      // can be a phone this time and a desktop last time.
      if (!p || !p.id || selected.size >= MAX) return;
      selected.set(String(p.id), {
        id: String(p.id),
        handle: p.handle || '',
        title: p.title || '',
        image: p.image || '',
        url: p.url || ''
      });
    });
    if (selected.size !== data.items.length) persist();
  }

  function cardData(input) {
    return {
      id: input.getAttribute('data-fm-compare-product'),
      handle: input.getAttribute('data-fm-compare-handle'),
      title: input.getAttribute('data-fm-compare-title') || '',
      image: input.getAttribute('data-fm-compare-image') || '',
      url: input.getAttribute('data-fm-compare-url') || ''
    };
  }

  /* ——— Selection ——— */
  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-fm-compare-product]');
    if (!input) return;
    var id = input.getAttribute('data-fm-compare-product');
    if (input.checked) {
      if (selected.size >= MAX) {
        input.checked = false;
        pulseLimit();
        return;
      }
      var data = cardData(input);
      selected.set(id, data);
      track('compare_product_added', { product_id: id, handle: data.handle, count: selected.size });
    } else {
      selected.delete(id);
    }
    syncCheckboxes();
    render();
  });

  // Remove a product from its chip.
  itemsEl.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.fm-compare__chip-remove');
    if (!btn) return;
    var chip = btn.closest('[data-fm-compare-chip]');
    if (!chip) return;
    selected.delete(chip.getAttribute('data-id'));
    syncCheckboxes();
    render();
  });

  // Clear the whole selection (mobile only — desktop deselects via the chips,
  // which the slim bar does not render).
  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      selected.clear();
      closeSheet();
      syncCheckboxes();
      render();
      track('compare_cleared', {});
    });
  }

  /* ——— Expand / collapse ———
   * Below 768px the Compare button opens the sheet; above it, the expanding
   * panel, unchanged. Both are gated on MIN, and the chevron toggle is hidden
   * on mobile so the two cannot both be open. */
  openBtn.addEventListener('click', function () {
    if (selected.size < MIN) return;
    if (isNarrow && sheetRoot) openSheet();
    else openPanel();
  });
  toggle.addEventListener('click', function () { expanded ? closePanel() : openPanel(); });
  closeBtn.addEventListener('click', closePanel);

  /* ——— Compare sheet ———
   * Re-parented to <body>. The section wrapper this markup is authored inside
   * creates a stacking context, which pins any descendant below the site header
   * no matter its own z-index — raising the z-index alone does nothing. Same
   * reason, same fix as the sort sheet.
   */
  if (sheetRoot && sheetRoot.parentElement !== document.body) {
    document.body.appendChild(sheetRoot);
  }

  if (sheetRoot) {
    // One hook for the scrim, the ✕, and anything else marked as a dismiss.
    sheetRoot.addEventListener('click', function (event) {
      if (event.target.closest('[data-fm-sheet-close]')) closeSheet();
    });

    // Remove a product from its column in the sticky header.
    if (sheetHead) {
      sheetHead.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-fm-compare-sheetremove]');
        if (!btn) return;
        selected.delete(btn.getAttribute('data-id'));
        syncCheckboxes();
        // render() both rebuilds the sheet and closes it once the count drops
        // below MIN — the brief's "removing the second-to-last product closes
        // the sheet", since at two selections removing one leaves one.
        render();
      });
    }

    // The grabber implies a pull-down; make it real, using the same threshold
    // as the other two sheets.
    if (sheetPanel && typeof window.fmAttachSheetDrag === 'function') {
      window.fmAttachSheetDrag(sheetPanel, closeSheet);
    }
  }

  function openSheet() {
    if (!sheetRoot) return;
    sheetOpen = true;
    sheetRoot.hidden = false;
    // Stop the grid behind the sheet scrolling under the finger.
    document.body.classList.add('fm-sheet-open');
    // The bar outranks the sheet on z-index (see the CSS note); it stands down.
    root.classList.add('is-sheet-open');
    track('compare_opened', { count: selected.size, handles: handles(), view: 'sheet' });
    ensureData().then(function () { if (sheetOpen) buildSheet(); });
  }

  function closeSheet() {
    if (!sheetRoot) return;
    sheetOpen = false;
    sheetRoot.hidden = true;
    document.body.classList.remove('fm-sheet-open');
    root.classList.remove('is-sheet-open');
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && sheetOpen) closeSheet();
  });

  function openPanel() {
    expanded = true;
    panel.hidden = false;
    root.classList.add('is-expanded');
    toggle.setAttribute('aria-expanded', 'true');
    track('compare_opened', { count: selected.size, handles: handles() });
    ensureData().then(buildTable);
  }
  function closePanel() {
    expanded = false;
    panel.hidden = true;
    root.classList.remove('is-expanded');
    toggle.setAttribute('aria-expanded', 'false');
  }

  function handles() {
    var out = [];
    selected.forEach(function (p) { out.push(p.handle); });
    return out;
  }

  /* ——— Data fetch (server-rendered ?view=compare) ——— */
  function ensureData() {
    var jobs = [];
    selected.forEach(function (p) {
      if (cache.has(p.handle)) return;
      var url = p.url.split('?')[0] + '?view=compare';
      jobs.push(
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (d) { cache.set(p.handle, d); })
          .catch(function () { cache.set(p.handle, { fields: [], error: true }); })
      );
    });
    return Promise.all(jobs);
  }

  /* ——— Render the bar ——— */
  function render() {
    var n = selected.size;
    /* Every mutation path — the change listener, the bar chips, the sheet's
     * remove buttons, Clear — ends in render(), so this is the one place the
     * store needs writing. */
    persist();
    root.hidden = n === 0;
    /* The bar instructs rather than just counting (design update): at one
     * selection it says what to do next, because "1 mat selected" with a dead
     * Compare button explains nothing. Desktop keeps the plain count. */
    if (introCount) {
      if (isNarrow && n === 1) {
        introCount.textContent = '1 ' + NOUN_LC.replace(/s$/, '') + ' added \u2014 pick one more';
      } else if (isNarrow) {
        introCount.textContent = n + ' ' + NOUN_LC + ' added';
      } else {
        introCount.textContent = n + ' ' + NOUN_LC + ' selected';
      }
    }
    if (btnLabel) btnLabel.textContent = isNarrow ? 'Compare' : 'Compare ' + n + ' ' + NOUN;
    openBtn.disabled = n < MIN;
    /* Styling hook for the muted/inert state — `disabled` alone left it looking
     * tappable at one selection. */
    openBtn.classList.toggle('is-inert', n < MIN);
    root.setAttribute('data-count', String(n));

    itemsEl.innerHTML = '';
    selected.forEach(function (p) { itemsEl.appendChild(chip(p)); });

    if (n < MIN && expanded) closePanel();
    if (expanded) ensureData().then(buildTable);

    /* Brief: "removing the second-to-last product closes the sheet". Expressed
     * as the MIN guard rather than a literal count, so it stays correct if the
     * minimum is ever raised — comparing one product is not comparing. */
    if (n < MIN && sheetOpen) closeSheet();
    if (sheetOpen) ensureData().then(function () { if (sheetOpen) buildSheet(); });
  }

  function chip(p) {
    var node = chipTpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-id', p.id);
    var img = node.querySelector('.fm-compare__chip-img');
    if (p.image) { img.src = p.image; img.alt = p.title; } else { img.remove(); }
    node.querySelector('.fm-compare__chip-name').textContent = p.title;
    return node;
  }

  /* ——— Render the comparison table ——— */
  function cell(cls, text) {
    var d = document.createElement('div');
    d.className = 'fm-compare__cell ' + (cls || '');
    if (text != null) d.textContent = text;
    return d;
  }

  /* Rating fields render as a row of circles (0–5, like review stars)
   * instead of text. A value only becomes circles when it's numeric and
   * within 0–5; non-numeric rating values (e.g. legacy "Good"/"Better"/
   * "Best") fall back to plain text until the metafield is rescored, so
   * mixed states render gracefully. */
  var RATING_MAX = 5;
  function parseRating(v) {
    if (v == null) return null;
    var s = String(v).trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    var n = parseFloat(s);
    if (isNaN(n) || n < 0 || n > RATING_MAX) return null;
    return n;
  }
  function ratingEl(score) {
    var wrap = document.createElement('span');
    wrap.className = 'fm-compare__rating';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', score + ' out of ' + RATING_MAX);
    var full = Math.floor(score);
    var frac = score - full;
    var half = frac >= 0.25 && frac < 0.75;
    var filled = frac >= 0.75 ? full + 1 : full;
    for (var i = 1; i <= RATING_MAX; i++) {
      var dot = document.createElement('span');
      var cls = 'fm-compare__rating-dot';
      if (i <= filled) cls += ' is-filled';
      else if (i === full + 1 && half) cls += ' is-half';
      dot.className = cls;
      wrap.appendChild(dot);
    }
    return wrap;
  }

  function buildTable() {
    var products = [];
    selected.forEach(function (p) { products.push(p); });
    if (!products.length) return;

    if (panelTitle) panelTitle.textContent = 'Compare ' + products.length + ' ' + NOUN;
    // Desktop: label + flexible product columns. Mobile (see CSS): fixed
    // widths so the table scrolls horizontally with a frozen label column.
    tableEl.style.setProperty('--fmcmp-cols', 'minmax(110px, 0.9fr) repeat(' + products.length + ', 1fr)');
    tableEl.style.setProperty('--fmcmp-cols-m', '128px repeat(' + products.length + ', 150px)');
    tableEl.innerHTML = '';

    var datas = products.map(function (p) { return cache.get(p.handle) || { fields: [] }; });

    // Union of field keys, in first-seen order, with their labels.
    var order = [];
    var labels = {};
    datas.forEach(function (d) {
      (d.fields || []).forEach(function (f) {
        if (!(f.key in labels)) { labels[f.key] = f.label; order.push(f.key); }
      });
    });
    // Per-product value lookup.
    var values = datas.map(function (d) {
      var m = {};
      (d.fields || []).forEach(function (f) { m[f.key] = f.value; });
      return m;
    });

    // Header row — blank label cell + product columns.
    tableEl.appendChild(cell('fm-compare__cell--head fm-compare__cell--label'));
    products.forEach(function (p) {
      var c = cell('fm-compare__cell--head');
      if (p.image) {
        var img = document.createElement('img');
        img.className = 'fm-compare__head-img';
        img.src = p.image; img.alt = p.title; img.loading = 'lazy';
        c.appendChild(img);
      }
      var name = document.createElement('span');
      name.className = 'fm-compare__head-name';
      name.textContent = p.title;
      c.appendChild(name);
      if (p.url) {
        var cta = document.createElement('a');
        cta.className = 'fm-compare__head-cta';
        cta.href = p.url;
        cta.textContent = 'View Options';
        c.appendChild(cta);
      }
      tableEl.appendChild(c);
    });

    // Striping resets at each group so the first row of a group is plain.
    var alt = false;
    function renderRow(label, cellValues, asRating) {
      var cls = alt ? ' fm-compare__cell--alt' : '';
      alt = !alt;
      tableEl.appendChild(cell('fm-compare__cell--label' + cls, label));
      cellValues.forEach(function (v) {
        var num = asRating ? parseRating(v) : null;
        if (num !== null) {
          var rc = cell('fm-compare__cell--value' + cls);
          rc.appendChild(ratingEl(num));
          tableEl.appendChild(rc);
        } else if (v != null && v !== '') {
          tableEl.appendChild(cell('fm-compare__cell--value' + cls, v));
        } else {
          var c = cell('fm-compare__cell--value' + cls);
          var dash = document.createElement('span');
          dash.className = 'fm-compare__empty';
          dash.textContent = '—';
          c.appendChild(dash);
          tableEl.appendChild(c);
        }
      });
    }
    function dataRow(key, label) {
      // Rating-keyed rows render numeric scores as circles (see ratingEl).
      var isRating = /rating/.test(String(key).toLowerCase());
      renderRow(label, values.map(function (m) { return m[key]; }), isRating);
    }
    function groupHeader(title) {
      var h = cell('fm-compare__group');
      // Text lives in an inner span so it can stay sticky-left while the
      // full-width grey bar spans the (horizontally-scrolling) table.
      var t = document.createElement('span');
      t.className = 'fm-compare__group-text';
      t.textContent = title;
      h.appendChild(t);
      tableEl.appendChild(h);
      alt = false;
    }
    function labelFor(key) { return LABELS[key] || labels[key]; }

    // 1. Headline rows, ungrouped, at the top: "Best for" (tagline) and
    //    "Delivery" (from each product's `delivery:` tag, resolved server-side).
    if (order.indexOf('tagline') !== -1) dataRow('tagline', labelFor('tagline'));
    if (datas.some(function (d) { return d.delivery; })) {
      renderRow('Delivery', datas.map(function (d) { return d.delivery; }));
    }

    // 2. Bucket remaining keys into groups (first matching group wins).
    var buckets = GROUPS.map(function () { return []; });
    order.forEach(function (key) {
      if (key === 'tagline') return;
      for (var gi = 0; gi < GROUPS.length; gi++) {
        var g = GROUPS[gi];
        if ((g.keys && g.keys.indexOf(key) !== -1) || g.test(key)) { buckets[gi].push(key); break; }
      }
    });
    GROUPS.forEach(function (g, gi) {
      if (!buckets[gi].length) return;            // skip empty groups
      groupHeader(g.title);
      buckets[gi].forEach(function (key) { dataRow(key, labelFor(key)); });
    });

    // 3. Pricing — always last, always present.
    groupHeader('Pricing');
    tableEl.appendChild(cell('fm-compare__cell--label fm-compare__cell--price', 'From Price'));
    datas.forEach(function (d) {
      var c = cell('fm-compare__cell--value fm-compare__cell--price');
      if (d.price) {
        var amt = document.createElement('span');
        amt.className = 'fm-compare__price-amount';
        amt.textContent = d.price;
        c.appendChild(amt);
        if (d.price_suffix) {
          var sfx = document.createElement('span');
          sfx.className = 'fm-compare__price-suffix';
          sfx.textContent = d.price_suffix;
          c.appendChild(sfx);
        }
      } else {
        c.textContent = '—';
      }
      tableEl.appendChild(c);
    });
  }

  /* ——— Render the mobile compare sheet ———
   * Three regions on identical grid tracks (`--fmcs-cols`) so the product
   * header, every row of values and the footer CTAs all line up in one set of
   * columns: the sticky header, the rows, and the footer.
   */
  function buildSheet() {
    if (!sheetRoot || !sheetHead || !sheetRows || !sheetFooter) return;
    var products = [];
    selected.forEach(function (p) { products.push(p); });
    if (products.length < MIN) return;

    var datas = products.map(function (p) { return cache.get(p.handle) || {}; });

    // One declaration, read by all three regions.
    if (sheetPanel) {
      sheetPanel.style.setProperty('--fmcs-cols', 'repeat(' + products.length + ', 1fr)');
    }

    // ——— Sticky product header ———
    sheetHead.innerHTML = '';
    products.forEach(function (p) {
      var col = document.createElement('div');
      col.className = 'fm-comparesheet__product';

      if (p.image) {
        var img = document.createElement('img');
        img.className = 'fm-comparesheet__img';
        img.src = p.image;
        img.alt = '';          // the title beneath carries the name
        img.loading = 'lazy';
        col.appendChild(img);
      }

      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'fm-comparesheet__remove';
      rm.setAttribute('data-fm-compare-sheetremove', '');
      rm.setAttribute('data-id', p.id);
      rm.setAttribute('aria-label', 'Remove ' + p.title + ' from the comparison');
      rm.innerHTML = '&times;';
      col.appendChild(rm);

      // A <div>, not a <p>: `.section p` in styles-min.scss.liquid restyles every
      // paragraph inside a section, and a <p> also carries UA margins that fight
      // the pinned two-line height.
      var name = document.createElement('div');
      name.className = 'fm-comparesheet__name';
      name.textContent = p.title;
      col.appendChild(name);

      sheetHead.appendChild(col);
    });

    // ——— Rows ———
    sheetRows.innerHTML = '';
    SHEET_ROWS.forEach(function (spec) {
      // Resolve each product's raw value for this row.
      var raw = datas.map(function (d) {
        if (spec.get) return spec.get(d);
        var found = null;
        (d.fields || []).forEach(function (f) {
          if (f.key === spec.field) found = f.value;
        });
        return found;
      });

      var hasAny = raw.some(function (v) { return v != null && String(v).trim() !== ''; });

      /* A row no selected product can answer is noise, so it is dropped
       * entirely rather than rendered as a line of dashes. This is what keeps
       * "Indoor or outdoor" invisible until the metafield exists, and it makes
       * the row appear on its own once it does. Price is exempt: a product
       * always has one, and a missing price is worth showing as missing. */
      if (!hasAny && !spec.priceRow && !spec.fallback) return;

      var row = document.createElement('div');
      row.className = 'fm-comparesheet__row';
      if (spec.priceRow) row.className += ' fm-comparesheet__row--price';

      var label = document.createElement('span');
      label.className = 'fm-comparesheet__rowlabel';
      label.textContent = spec.label;
      row.appendChild(label);

      var values = document.createElement('div');
      values.className = 'fm-comparesheet__values';

      raw.forEach(function (v, i) {
        var cellEl = document.createElement('div');
        cellEl.className = 'fm-comparesheet__value';

        if (spec.ratingRow) {
          renderSheetRating(cellEl, datas[i]);
        } else if (spec.priceRow) {
          renderSheetPrice(cellEl, datas[i]);
        } else if (v != null && String(v).trim() !== '') {
          cellEl.textContent = spec.format ? spec.format(v) : String(v);
        } else if (spec.fallback) {
          cellEl.textContent = spec.fallback;
        } else {
          var dash = document.createElement('span');
          dash.className = 'fm-comparesheet__empty';
          dash.textContent = '—';
          cellEl.appendChild(dash);
        }

        values.appendChild(cellEl);
      });

      row.appendChild(values);

      /* Difference highlighting — the reason the sheet exists. Compared on the
       * DISPLAYED text, not the raw metafield, so "9" and "9mm" do not read as
       * a difference; and case/whitespace-insensitively, because editor text is
       * inconsistent ("Next Day" vs "next day" is not a real difference). */
      if (differs(raw, spec, datas)) row.className += ' is-diff';

      sheetRows.appendChild(row);
    });

    // ——— Footer: one CTA per column, on the same tracks ———
    sheetFooter.innerHTML = '';
    products.forEach(function (p) {
      var cta = document.createElement('a');
      cta.className = 'fm-comparesheet__cta';
      cta.href = p.url || '#';
      cta.textContent = 'View product';
      cta.setAttribute('aria-label', 'View ' + p.title);
      sheetFooter.appendChild(cta);
    });
  }

  function normalise(v) {
    return String(v == null ? '' : v).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function differs(raw, spec, datas) {
    var comparable = raw.map(function (v, i) {
      if (spec.diff) return normalise(spec.diff(datas[i]));
      if (spec.ratingRow) return normalise(ratingText(datas[i]));
      if (v == null || String(v).trim() === '') return normalise(spec.fallback || '');
      return normalise(spec.format ? spec.format(v) : v);
    });
    return comparable.some(function (v) { return v !== comparable[0]; });
  }

  function renderSheetPrice(cellEl, d) {
    if (!d.price) {
      cellEl.textContent = '—';
      return;
    }
    cellEl.appendChild(document.createTextNode(d.price));
    if (d.price_suffix) {
      var sfx = document.createElement('span');
      sfx.className = 'fm-comparesheet__suffix';
      sfx.textContent = d.price_suffix;
      cellEl.appendChild(sfx);
    }
  }

  /* Rating renders as a figure plus its review count, not as stars.
   *
   * Two reasons. At three columns on a 390px screen each column is ~113px, and
   * five glyphs plus a count crowds it. More importantly every entrance mat
   * currently scores 5, so stars would draw five identical rows of five and
   * look like a rendering bug — the count is the part that actually differs,
   * and digits rank better than glyphs anyway. Noted as a deviation in the PR.
   */
  /* Shown exactly as stored — no rounding and no forced decimal place. An
   * earlier `toFixed(1)` turned 4.85 into "4.9", and the row is meant to let you
   * rank the actual averages. The view now sends `rating_override_number`, the
   * decimal, rather than the rounded `_stars` choice value. */
  function ratingScore(d) {
    if (d.rating == null || d.rating === '') return '';
    return String(d.rating).trim();
  }

  function ratingText(d) {
    var score = ratingScore(d);
    if (!score) return '';
    return d.rating_count ? score + ' (' + d.rating_count + ')' : score;
  }

  function renderSheetRating(cellEl, d) {
    if (d.rating == null || d.rating === '') {
      var none = document.createElement('span');
      none.className = 'fm-comparesheet__empty';
      // Absence here means "not reviewed yet", which is worth saying plainly
      // rather than leaving a dash to be read as missing data.
      none.textContent = 'No reviews yet';
      cellEl.appendChild(none);
      return;
    }
    cellEl.appendChild(document.createTextNode(ratingScore(d)));
    if (d.rating_count) {
      var c = document.createElement('span');
      c.className = 'fm-comparesheet__count';
      c.textContent = ' (' + d.rating_count + ')';
      cellEl.appendChild(c);
    }
  }

  /* ——— Keep checkboxes in step with state ——— */
  function syncCheckboxes() {
    var atMax = selected.size >= MAX;
    document.querySelectorAll('[data-fm-compare-product]').forEach(function (cb) {
      var on = selected.has(cb.getAttribute('data-fm-compare-product'));
      cb.checked = on;
      cb.disabled = !on && atMax;
    });
  }

  function pulseLimit() {
    openBtn.classList.remove('fm-compare__compare-btn--pulse');
    // force reflow so the animation can retrigger
    void openBtn.offsetWidth;
    openBtn.classList.add('fm-compare__compare-btn--pulse');
  }

  // The facet/sort/paginate flow swaps #ProductGridContainer's innerHTML,
  // recreating the checkboxes. Re-apply checked state after each swap.
  var grid = document.getElementById('ProductGridContainer');
  if (grid && 'MutationObserver' in window) {
    var raf = 0;
    new MutationObserver(function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; syncCheckboxes(); });
    }).observe(grid, { childList: true, subtree: true });
  }

  // Rehydrate the selection made before this navigation, then fold in anything
  // the server rendered as already checked, then paint. syncCheckboxes() is what
  // re-ticks the boxes for restored products that appear on THIS page; restored
  // products from another page simply have no checkbox here, which is fine — the
  // bar and the sheet both work from the stored handle and url.
  restore();
  document.querySelectorAll('[data-fm-compare-product]:checked').forEach(function (cb) {
    var id = cb.getAttribute('data-fm-compare-product');
    if (!selected.has(id) && selected.size < MAX) selected.set(id, cardData(cb));
  });
  syncCheckboxes();
  render();
})();
